import React, { useState } from 'react';
import { Typography, Paper, Box, useTheme, useMediaQuery, Grid, Container } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { Dayjs } from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import DayInfoForm from './components/DayInfoForm';
import DayMetricsForm from './components/widgets/DayMetricsForm';
import MealLogger from './components/widgets/MealLogger';
import DailyTasks from './components/widgets/DailyTasks';
import DailiesList from './components/widgets/DailiesList';
import DayEvents from './components/widgets/DayEvents';
import Layout from '../../components/Layout';

const DayView: React.FC = () => {
  const { date } = useParams<{ date?: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const initialDate = date ? dayjs(date) : dayjs();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(initialDate.isValid() ? initialDate : dayjs());

  const handleDateChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setSelectedDate(newValue);
      navigate(`/day-view/${newValue.format('YYYY-MM-DD')}`, { replace: true });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Layout>
        <Container maxWidth="xl" sx={{ mt: 0 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
              Day View
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Managing: {selectedDate.format('dddd, D MMMM YYYY')}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Left Column: Calendar & Daily Habits */}
            <Grid item xs={12} lg={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, position: { lg: 'sticky' }, top: 24 }}>
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ px: 1, mb: 1, fontWeight: 'medium' }}>
                    Select Date
                  </Typography>
                  <Box sx={{ 
                    '& .MuiDateCalendar-root': { width: '100%', height: 'auto', maxHeight: 'none' },
                    '& .MuiPickersDay-root.Mui-selected': { backgroundColor: 'primary.main' }
                  }}>
                    <DateCalendar
                      value={selectedDate}
                      onChange={handleDateChange}
                    />
                  </Box>
                </Paper>

                <DailiesList selectedDate={selectedDate} />
              </Box>
            </Grid>

            {/* Center Column: Journal, Metrics, Meals */}
            <Grid item xs={12} lg={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <DayInfoForm selectedDate={selectedDate} />
                <DayMetricsForm selectedDate={selectedDate} />
                <MealLogger selectedDate={selectedDate} />
              </Box>
            </Grid>

            {/* Right Column: Tasks & Events */}
            <Grid item xs={12} lg={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, position: { lg: 'sticky' }, top: 24 }}>
                <DailyTasks selectedDate={selectedDate} />
                <DayEvents selectedDate={selectedDate} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Layout>
    </LocalizationProvider>
  );
};

export default DayView;
